import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import api from "../lib/api";
import { fmtH as fmtOreCent } from "../lib/planning";
import { Button } from "../components/ui/button";
import {
    ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Clock4, List, Loader2, LogIn, LogOut,
    Zap, Plane, AlertTriangle,
} from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const FLAG_LABELS = {
    uscita_mancante: "Uscita mancante",
    entrata_mancante: "Entrata mancante",
    fuori_assegnazione: "Fuori assegnazione",
    in_corso: "In corso",
};
const EXTRA_BADGES = {
    assegnata: { label: "ASSEGNATA", cls: "bg-sky-100 text-sky-700" },
    da_confermare: { label: "IN VERIFICA", cls: "bg-amber-100 text-amber-700" },
    confermata: { label: "CONFERMATA", cls: "bg-emerald-100 text-emerald-700" },
};
const fmtH = (h) => fmtOreCent(h ?? 0);
const fmtTime = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
    } catch { return "—"; }
};
const fmtDate = (iso) => {
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    } catch { return iso; }
};

export default function OreDipendentePage() {
    const { user } = useAuth();
    const company = useCompany();
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(() => {
        try { return localStorage.getItem("ss_ore_view") || "elenco"; } catch { return "elenco"; }
    });
    const [selectedDay, setSelectedDay] = useState(null);

    const switchView = (v) => {
        setView(v);
        try { localStorage.setItem("ss_ore_view", v); } catch (e) { console.warn("Preferenza vista non salvata", e); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/presenze/me/riepilogo", { params: { year, month } });
            setData(data);
        } catch { setData(null); }
        finally { setLoading(false); }
    }, [year, month]);

    useEffect(() => { load(); }, [load]);

    const shiftMonth = (n) => {
        let m = month + n, y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
        setSelectedDay(null);
    };

    const dayKeys = Object.keys(data?.days || {});
    const empty = !loading && dayKeys.length === 0 && !(data?.extras?.length) && !(data?.leaves?.length);

    return (
        <div className="min-h-screen bg-[#F4F8FD]" data-testid="ore-page">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
                    <div>
                        <Link to="/portale" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#376DB3]" data-testid="ore-back-link">
                            <ArrowLeft className="h-3.5 w-3.5" /> Torna al portale
                        </Link>
                        <h1 className="mt-1 font-display text-xl font-bold text-slate-800">Le mie ore</h1>
                        <p className="text-xs text-slate-500">{user?.name} · {company.ragione_sociale}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(-1)} data-testid="ore-prev-month">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="min-w-[130px] text-center font-display text-sm font-bold text-slate-700" data-testid="ore-month-label">
                            {MONTHS[month - 1]} {year}
                        </span>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shiftMonth(1)} data-testid="ore-next-month">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <Kpi label="Ore lavorate" value={`${fmtH(data?.ore_lavorate)} h`} color="text-[#376DB3]" testid="ore-kpi-lavorate" />
                            <Kpi label="Straordinari" value={`${fmtH(data?.ore_straordinari)} h`} color="text-violet-600" testid="ore-kpi-extra" />
                            <Kpi label="Giorni di ferie" value={data?.giorni_ferie ?? 0} color="text-sky-600" testid="ore-kpi-ferie" />
                            <Kpi label="Permessi" value={data?.permessi ?? 0} color="text-amber-600" testid="ore-kpi-permessi" />
                        </div>

                        {empty && (
                            <div className="mt-6 rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="ore-empty">
                                Nessun dato registrato per questo mese.
                            </div>
                        )}

                        <div className="mt-6 flex items-center justify-between">
                            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm" data-testid="ore-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => switchView("elenco")}
                                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "elenco" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:text-slate-700"}`}
                                    data-testid="ore-view-elenco-btn"
                                >
                                    <List className="h-3.5 w-3.5" /> Elenco
                                </button>
                                <button
                                    type="button"
                                    onClick={() => switchView("calendario")}
                                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "calendario" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:text-slate-700"}`}
                                    data-testid="ore-view-calendario-btn"
                                >
                                    <CalendarDays className="h-3.5 w-3.5" /> Calendario
                                </button>
                            </div>
                        </div>

                        {view === "calendario" && !empty && (
                            <MonthCalendar
                                year={year}
                                month={month}
                                days={data?.days || {}}
                                leaves={data?.leaves || []}
                                extras={data?.extras || []}
                                selectedDay={selectedDay}
                                onSelectDay={setSelectedDay}
                            />
                        )}

                        {view === "elenco" && dayKeys.length > 0 && (
                            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                                <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                                    <Clock4 className="h-4 w-4 text-[#376DB3]" /> Giornate lavorate
                                </div>
                                <ul className="divide-y divide-slate-100" data-testid="ore-days-list">
                                    {dayKeys.map((k) => {
                                        const d = data.days[k];
                                        return (
                                            <li key={k} className="px-4 py-3" data-testid={`ore-day-${k}`}>
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold capitalize text-slate-700">{fmtDate(k)}</span>
                                                    <span className="font-mono-numbers text-sm font-bold text-slate-800">{fmtH(d.ore)} h</span>
                                                </div>
                                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                                                    {d.entries.map((e) => (
                                                        <span key={e.id} className={`inline-flex items-center gap-1 ${e.type === "in" ? "text-emerald-700" : "text-red-600"}`}>
                                                            {e.type === "in" ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
                                                            {fmtTime(e.at)}
                                                            <span className="text-slate-400">{e.worksite_name}</span>
                                                        </span>
                                                    ))}
                                                    {d.flags.map((f) => (
                                                        <span key={f} className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                                            <AlertTriangle className="h-2.5 w-2.5" /> {FLAG_LABELS[f] || f}
                                                        </span>
                                                    ))}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {data?.extras?.length > 0 && (
                            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                                <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                                    <Zap className="h-4 w-4 text-violet-600" /> Straordinari del mese
                                </div>
                                <ul className="divide-y divide-slate-100" data-testid="ore-extras-list">
                                    {data.extras.map((x) => {
                                        const b = EXTRA_BADGES[x.status] || {};
                                        return (
                                            <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                                                <div className="min-w-0">
                                                    <span className="capitalize text-slate-700">{fmtDate(x.date)}</span>
                                                    <span className="ml-2 font-mono-numbers text-xs font-semibold text-slate-600">{x.hours} h</span>
                                                    {x.worksite_name && <span className="ml-2 text-xs text-slate-500">{x.worksite_name}</span>}
                                                    <div className="truncate text-xs text-slate-400">{x.description}</div>
                                                </div>
                                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`}>{b.label}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {data?.leaves?.length > 0 && (
                            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                                <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                                    <Plane className="h-4 w-4 text-sky-600" /> Assenze approvate
                                </div>
                                <ul className="divide-y divide-slate-100" data-testid="ore-leaves-list">
                                    {data.leaves.map((l) => (
                                        <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                                            <div>
                                                <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${l.type === "ferie" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                                    {l.type === "ferie" ? "FERIE" : "PERMESSO"}
                                                </span>
                                                <span className="capitalize text-slate-700">
                                                    {l.date_from === l.date_to ? fmtDate(l.date_from) : `${fmtDate(l.date_from)} → ${fmtDate(l.date_to)}`}
                                                </span>
                                                {l.time_from && <span className="ml-1 font-mono-numbers text-xs text-slate-500">({l.time_from}–{l.time_to || "…"})</span>}
                                            </div>
                                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">APPROVATA</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

function Kpi({ label, value, color, testid }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
            <div className={`mt-1 font-mono-numbers text-2xl font-bold ${color}`}>{value}</div>
        </div>
    );
}

const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function MonthCalendar({ year, month, days, leaves, extras, selectedDay, onSelectDay }) {
    const cells = useMemo(() => {
        const startDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
        const daysInMonth = new Date(year, month, 0).getDate();
        const out = [];
        for (let i = 0; i < startDow; i++) out.push(null);
        for (let d = 1; d <= daysInMonth; d++) out.push(d);
        while (out.length % 7 !== 0) out.push(null);
        return out;
    }, [year, month]);

    const { leaveByDate, extraByDate } = useMemo(() => {
        const lm = {}, xm = {};
        for (const l of leaves) {
            const from = new Date(`${l.date_from}T00:00:00`);
            const to = new Date(`${l.date_to || l.date_from}T00:00:00`);
            for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
                if (d.getFullYear() === year && d.getMonth() === month - 1) {
                    lm[d.getDate()] = l.type;
                }
            }
        }
        for (const x of extras) {
            const d = new Date(`${x.date}T00:00:00`);
            if (d.getFullYear() === year && d.getMonth() === month - 1) {
                xm[d.getDate()] = (xm[d.getDate()] || 0) + (Number(x.hours) || 0);
            }
        }
        return { leaveByDate: lm, extraByDate: xm };
    }, [leaves, extras, year, month]);

    const todayKey = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
    const keyOf = (d) => `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const selected = selectedDay ? days[selectedDay] : null;

    return (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="ore-calendar">
            <div className="grid grid-cols-7 border-b border-slate-100">
                {DOW.map((d) => (
                    <div key={d} className="py-2 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((d, i) => {
                    if (d === null) return <div key={`e${i}`} className="min-h-[64px] border-b border-r border-slate-50 bg-slate-50/50 sm:min-h-[76px]" />;
                    const k = keyOf(d);
                    const info = days[k];
                    const leave = leaveByDate[d];
                    const extra = extraByDate[d];
                    const isToday = k === todayKey;
                    const isSelected = selectedDay === k;
                    const hasFlags = info?.flags?.length > 0;
                    const clickable = !!info;
                    return (
                        <button
                            key={k}
                            type="button"
                            disabled={!clickable}
                            onClick={() => onSelectDay(isSelected ? null : k)}
                            className={`relative flex min-h-[64px] flex-col items-center border-b border-r border-slate-50 px-0.5 py-1.5 text-center transition-colors sm:min-h-[76px]
                                ${isSelected ? "bg-[#376DB3]/10 ring-2 ring-inset ring-[#376DB3]" : clickable ? "hover:bg-slate-50" : ""}
                                ${leave === "ferie" ? "bg-sky-50" : leave ? "bg-violet-50" : ""}`}
                            data-testid={`ore-cal-day-${k}`}
                        >
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${isToday ? "bg-[#376DB3] text-white" : "text-slate-500"}`}>
                                {d}
                            </span>
                            {info && (
                                <span className="mt-0.5 font-mono-numbers text-xs font-bold text-[#376DB3] sm:text-sm">
                                    {fmtH(info.ore)}h
                                </span>
                            )}
                            {extra > 0 && (
                                <span className="font-mono-numbers text-[10px] font-bold text-violet-600">+{fmtH(extra)}h</span>
                            )}
                            {leave && (
                                <span className={`mt-0.5 rounded px-1 text-[8px] font-bold sm:text-[9px] ${leave === "ferie" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                    {leave === "ferie" ? "FERIE" : "PERM."}
                                </span>
                            )}
                            {hasFlags && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-500" />}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 px-4 py-2 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1"><span className="font-mono-numbers font-bold text-[#376DB3]">0h</span> ore timbrate</span>
                <span className="inline-flex items-center gap-1"><span className="font-mono-numbers font-bold text-violet-600">+0h</span> straordinari</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-sky-100 ring-1 ring-sky-300" /> ferie</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-violet-100 ring-1 ring-violet-300" /> permesso</span>
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> anomalia</span>
            </div>

            {selected && (
                <div className="border-t border-slate-100 px-4 py-3" data-testid="ore-cal-detail">
                    <div className="flex items-center justify-between">
                        <span className="font-semibold capitalize text-slate-700">{fmtDate(selectedDay)}</span>
                        <span className="font-mono-numbers text-sm font-bold text-slate-800">{fmtH(selected.ore)} h</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        {selected.entries.map((e) => (
                            <span key={e.id} className={`inline-flex items-center gap-1 ${e.type === "in" ? "text-emerald-700" : "text-red-600"}`}>
                                {e.type === "in" ? <LogIn className="h-3 w-3" /> : <LogOut className="h-3 w-3" />}
                                {fmtTime(e.at)}
                                <span className="text-slate-400">{e.worksite_name}</span>
                            </span>
                        ))}
                        {selected.flags.map((f) => (
                            <span key={f} className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                <AlertTriangle className="h-2.5 w-2.5" /> {FLAG_LABELS[f] || f}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
